/**
 * Cliente del API para el panel de administración.
 *
 * A diferencia de la PWA, aquí no hay cola ni caché offline: el panel se usa en
 * oficina con red. Lo que sí comparte es la renovación de token y el manejo de
 * errores por campo, para que los formularios muestren el mensaje junto al
 * control correspondiente.
 */

import type { GpsDetectionResult } from '@capi/shared';

const TOKEN_KEY = 'capi.admin.accessToken';
const REFRESH_KEY = 'capi.admin.refreshToken';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const session = {
  get accessToken() {
    return localStorage.getItem(TOKEN_KEY);
  },
  get refreshToken() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = session.refreshToken;
  if (!refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        session.clear();
        return false;
      }
      const data = (await response.json()) as { accessToken: string; refreshToken: string };
      session.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  anonymous?: boolean;
  skipRefresh?: boolean;
  /** Devuelve texto crudo en lugar de JSON (CSV, sintaxis de SPSS). */
  raw?: boolean;
}

export async function request<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, skipRefresh = false, raw = false } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (!anonymous && session.accessToken) {
    headers['Authorization'] = `Bearer ${session.accessToken}`;
  }

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 401 && !anonymous && !skipRefresh) {
    if (await refreshSession()) {
      return request<T>(path, { ...options, skipRefresh: true });
    }
    session.clear();
    window.location.reload();
  }

  if (response.status === 204) return undefined as T;

  if (raw) {
    const text = await response.text();
    if (!response.ok) throw new ApiError(response.status, text);
    return text as T;
  }

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    const detail = payload as {
      message?: string | string[];
      fieldErrors?: Record<string, string[]>;
    } | null;
    const message = Array.isArray(detail?.message)
      ? detail.message.join(' ')
      : (detail?.message ?? `Error ${response.status}`);
    throw new ApiError(response.status, message, detail?.fieldErrors);
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/* -------------------------------------------------------------------------- */
/*                                  Tipos                                     */
/* -------------------------------------------------------------------------- */

export interface AdminUser {
  id: string;
  code: string;
  name: string;
  email: string | null;
  role: string;
  roleLabel: string;
  isActive: boolean;
  mustChangePassword: boolean;
  birthdayRule: 'proximo' | 'ultimo' | null;
  phone: string | null;
  notes: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  deactivatedAt: string | null;
  supervisor: { id: string; code: string; name: string } | null;
  interviewCount: number;
  assignmentCount: number;
  teamSize: number;
}

export interface FrameOverview {
  strata: {
    id: string;
    name: string;
    description: string;
    sectionCount: number;
    selectedSectionCount: number;
    listaNominal2024: number;
    padronShare: number;
    participation2024: number;
    politicalProfile: { coalicionMorena2024: number; pesBc2024: number; pan2024: number };
    targetN: number;
    clusterCount: number;
    clusterInterviews: number;
    pps: { interval: number; randomStart: number };
    coverageOfStratum: number;
    marginOfError: { theoretical: number; effective: number; documented: number };
    readAsTrendOnly: boolean;
  }[];
  totals: {
    sections: number;
    selectedSections: number;
    listaNominal2024: number;
    clusters: number;
    targetInterviews: number;
    padronCoverage: number;
    marginOfError: { theoretical: number; effective: number };
  };
  documented: Record<string, number>;
  consistency: {
    check: string;
    expected: number;
    actual: number;
    ok: boolean;
    reference: string;
  }[];
  sectionsNotInFrame: number[];
}

export interface MonitoringOverview {
  wave: { id: string; code: string; label: string; targetN: number; programmedN: number; status: string };
  counts: {
    completed: number;
    partial: number;
    inProgress: number;
    annulled: number;
    pilot: number;
    annulmentRate: number;
    expectedAnnulmentRate: number;
  };
  progress: { target: number; programmed: number; completed: number; remaining: number; share: number };
  marginOfError: { theoretical: number; effective: number; designEffect: number; note: string };
  duration: {
    averageMinutes: number | null;
    minMinutes: number | null;
    maxMinutes: number | null;
    designRange: { min: number; max: number };
    minimumAllowed: number;
  };
}

export interface CartographyStatus {
  activeImport: {
    id: string;
    filename: string;
    createdAt: string;
    featureCount: number;
    report: unknown;
  } | null;
  layers: {
    level: string;
    label: string;
    source: string;
    loaded: boolean;
    featureCount: number;
    fallbackLevel: string | null;
  }[];
  finestLevel: string;
  postgisAvailable: boolean;
  coverage: {
    missingFromCartography: number[];
    unmappedInCartography: number[];
    resolvedByEquivalence: number[];
    unverifiedGroups: number[];
  };
  equivalences: {
    id: string;
    frameSeccion: number;
    currentSeccion: number;
    reason: string;
    verified: boolean;
    note: string | null;
  }[];
  frameSectionCount: number;
  loadedSectionCount: number;
}

/* -------------------------------------------------------------------------- */

export const api = {
  /* ------------------------------- Acceso ------------------------------- */
  login: (identifier: string, password: string) =>
    request<{
      accessToken: string;
      refreshToken: string;
      user: {
        id: string;
        code: string;
        name: string;
        role: string;
        mustChangePassword: boolean;
        permissions: string[];
      };
    }>('/auth/login', {
      method: 'POST',
      anonymous: true,
      body: { identifier, password, app: 'admin' },
    }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  me: () =>
    request<{
      id: string;
      code: string;
      name: string;
      role: string;
      roleLabel: string;
      mustChangePassword: boolean;
      permissions: string[];
    }>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword, confirmPassword: newPassword },
    }),

  /* ------------------------------ Usuarios ------------------------------ */
  users: (params: Record<string, string> = {}) =>
    request<AdminUser[]>(`/users?${new URLSearchParams(params)}`),

  roles: () =>
    request<{ id: string; label: string; description: string; permissions: string[] }[]>(
      '/users/roles',
    ),

  createUser: (body: Record<string, unknown>) =>
    request<{ user: AdminUser; temporaryPassword: string | null }>('/users', {
      method: 'POST',
      body,
    }),

  updateUser: (id: string, body: Record<string, unknown>) =>
    request<AdminUser>(`/users/${id}`, { method: 'PATCH', body }),

  setUserActive: (id: string, isActive: boolean) =>
    request<AdminUser>(`/users/${id}/active`, { method: 'PATCH', body: { isActive } }),

  resetPassword: (id: string) =>
    request<{ temporaryPassword: string | null }>(`/users/${id}/reset-password`, {
      method: 'POST',
      body: {},
    }),

  revokeSessions: (id: string) =>
    request<{ revoked: number }>(`/users/${id}/revoke-sessions`, { method: 'POST', body: {} }),

  fieldTeam: (waveId?: string) =>
    request<
      {
        id: string;
        code: string;
        name: string;
        birthdayRule: string | null;
        supervisor: { id: string; code: string; name: string } | null;
        assignments: {
          id: string;
          status: string;
          targetInterviews: number;
          capturedInterviews: number;
          cluster: { id: string; seccion: number; reference: string; stratumId: string };
        }[];
        totals: {
          clusters: number;
          targetInterviews: number;
          capturedInterviews: number;
          progress: number;
        };
      }[]
    >(`/users/field-team${waveId ? `?waveId=${waveId}` : ''}`),

  /* -------------------------------- Marco ------------------------------- */
  frameOverview: () => request<FrameOverview>('/frame/overview'),

  sections: (params: Record<string, string> = {}) =>
    request<
      {
        seccion: number;
        stratum: { id: string; name: string };
        colonia: string;
        coloniaConfirmed: boolean;
        casillas: number;
        casillasExtraordinarias: number;
        listaNominal2024: number;
        listaNominal2026Estimated: number | null;
        padronShare: number;
        participation2024: number;
        coalicionMorena2024: number;
        pesBc2024: number;
        selected: boolean;
        clusterCount: number;
        targetInterviews: number;
        units: {
          id: string;
          areaCode: string;
          listaNominal2024: number;
          selected: boolean;
          splitPending: boolean;
        }[];
        hasGeometry: boolean;
        cartographySecciones: number[];
      }[]
    >(`/frame/sections?${new URLSearchParams(params)}`),

  clusters: (params: Record<string, string> = {}) =>
    request<
      {
        id: string;
        stratumId: string;
        seccion: number;
        colonia: string;
        areaCode: string;
        areaLabel: string;
        unitId: string;
        unitListaNominal: number;
        reference: string;
        targetInterviews: number;
        manzanasToSelect: number;
        estimatedDwellings: number | null;
        systematicSkipK: number | null;
        manzanaSelections: {
          id: string;
          cvegeo: string | null;
          orderIndex: number;
          estimatedDwellings: number | null;
          targetInterviews: number | null;
        }[];
        assignments: {
          id: string;
          status: string;
          targetInterviews: number;
          encuestador: { id: string; code: string; name: string };
        }[];
        interviewCount: number;
        hasArea: boolean;
      }[]
    >(`/frame/clusters?${new URLSearchParams(params)}`),

  equivalences: () =>
    request<
      {
        frameSeccion: number;
        currentSecciones: number[];
        reason: string;
        verified: boolean;
        note: string | null;
      }[]
    >('/frame/equivalences'),

  saveEquivalence: (body: Record<string, unknown>) =>
    request<unknown>('/frame/equivalences', { method: 'PUT', body }),

  setDwellings: (clusterId: string, estimatedDwellings: number) =>
    request<{ systematicSkipK: number }>(`/frame/clusters/${clusterId}/dwellings`, {
      method: 'PATCH',
      body: { estimatedDwellings },
    }),

  frameTargets: () =>
    request<{
      gender: TargetRow[];
      ageRanges: TargetRow[];
      ageGroups: TargetRow[];
      education: TargetRow[];
      educationValuesAreApproximate: boolean;
      educationNote: string;
      areQuotas: boolean;
      usageNote: string;
    }>('/frame/targets'),

  /* ----------------------------- Cartografía ---------------------------- */
  cartographyStatus: () => request<CartographyStatus>('/cartography/status'),

  cartographyImports: () =>
    request<
      {
        id: string;
        kind: string;
        filename: string;
        sourceName: string | null;
        featureCount: number;
        isActive: boolean;
        createdAt: string;
        report: unknown;
        importedBy: { id: string; name: string; code: string } | null;
      }[]
    >('/cartography/imports'),

  importSections: (body: Record<string, unknown>) =>
    request<{
      importId: string;
      featureCount: number;
      blockingIssues: string[];
      warnings: string[];
      coverage: CartographyStatus['coverage'];
      unmappedSections: number[];
    }>('/cartography/imports/sections', { method: 'POST', body }),

  importManzanas: (body: Record<string, unknown>) =>
    request<{
      importId: string;
      featureCount: number;
      assignedToSection: number;
      unassigned: number;
      warnings: string[];
    }>('/cartography/imports/manzanas', { method: 'POST', body }),

  /* ------------------------------ Monitoreo ----------------------------- */
  monitoringOverview: () => request<MonitoringOverview>('/monitoring/overview'),

  monitoringStrata: () =>
    request<
      {
        id: string;
        name: string;
        target: number;
        completed: number;
        remaining: number;
        share: number;
        marginOfError: { theoretical: number; effective: number };
        documentedMarginOfError: number;
        readAsTrendOnly: boolean;
      }[]
    >('/monitoring/strata'),

  monitoringClusters: () =>
    request<
      {
        id: string;
        seccion: number;
        stratumId: string;
        areaCode: string;
        reference: string;
        target: number;
        completed: number;
        remaining: number;
        share: number;
        householdsContacted: number;
        estimatedDwellings: number | null;
        manzanasSelected: number;
        hasArea: boolean;
        /** Varias cuando el conglomerado se reparte entre encuestadores. */
        assignments: {
          id: string;
          status: string;
          systematicSkipK: number | null;
          targetInterviews: number;
          _count: { interviews: number; manzanas: number };
          encuestador: { id: string; code: string; name: string };
        }[];
      }[]
    >('/monitoring/clusters'),

  results: (filtros: Record<string, string> = {}) =>
    request<ResultsPreview>(`/monitoring/results?${new URLSearchParams(filtros)}`),

  workload: () => request<WorkloadResponse>('/monitoring/workload'),

  targetDeviation: () =>
    request<{
      total: number;
      gender: DeviationRow[];
      ageRanges: DeviationRow[];
      ageGroups: DeviationRow[];
      education: DeviationRow[];
      educationNeedsRaking: boolean;
      educationRakingThresholdPp: number;
      areQuotas: boolean;
      correctionNote: string;
      rescheduleReviewDay: number;
    }>('/monitoring/target-deviation'),

  responseRates: () =>
    request<{
      overall: {
        tally: Record<string, number>;
        responseRateRR3: number;
        refusalRateREF2: number;
        contactRateCON2: number;
      };
      byStratum: {
        stratumId: string;
        name: string;
        tally: Record<string, number>;
        responseRateRR3: number;
        refusalRateREF2: number;
        contactRateCON2: number;
      }[];
      note: string;
    }>('/monitoring/response-rates'),

  interviewers: () =>
    request<{
      interviewers: {
        id: string;
        code: string;
        name: string;
        isActive: boolean;
        completed: number;
        annulled: number;
        householdsContacted: number;
        refusalRate: number;
        responseRate: number;
        averageDurationMinutes: number | null;
        qualityFlagCount: number;
        flagsPerInterview: number;
        refusalRateZScore: number | null;
        refusalRateAtypical: boolean;
      }[];
      teamAverages: { refusalRate: number; refusalRateSd: number; interviewersWithWork: number };
    }>('/monitoring/interviewers'),

  dailyProgress: () =>
    request<{
      days: { day: string; total: number; byInterviewer: { code: string; completed: number }[] }[];
      expectedPerInterviewerPerDay: { urban: { min: number; max: number }; rural: { min: number; max: number } };
    }>('/monitoring/daily'),

  captureMap: (params: Record<string, string> = {}) =>
    request<{
      type: 'FeatureCollection';
      features: {
        type: 'Feature';
        geometry: { type: 'Point'; coordinates: [number, number] };
        properties: Record<string, unknown>;
      }[];
    }>(`/monitoring/capture-map?${new URLSearchParams(params)}`),

  experimentBalance: () =>
    request<{
      cells: {
        cellId: string;
        personajeId: string;
        labelText: string;
        assigned: number;
        ineligible: number;
        effectiveBase: number;
      }[];
    }>('/monitoring/experiment-balance'),

  i6Coverage: () =>
    request<{ personajeId: string; responses: number }[]>('/monitoring/i6-coverage'),

  /* ------------------------------ Calidad ------------------------------- */
  flagSummary: () =>
    request<
      { flag: string; label: string; severity: string; total: number; open: number }[]
    >('/quality/flags/summary'),

  flags: (params: Record<string, string> = {}) =>
    request<{
      total: number;
      items: {
        id: string;
        flag: string;
        label: string;
        severity: string;
        detail: Record<string, unknown> | null;
        createdAt: string;
        resolvedAt: string | null;
        resolution: string | null;
        interview: {
          id: string;
          clientUuid: string;
          status: string;
          startedAt: string;
          durationMinutes: number | null;
          gpsStatus: string | null;
          distanceToExpectedMeters: number | null;
          outOfPolygonReason: string | null;
          outOfPolygonNote: string | null;
          expectedFrameSeccion: number | null;
          detectedFrameSeccion: number | null;
          encuestador: { id: string; code: string; name: string };
          cluster: { id: string; seccion: number; reference: string };
        };
        resolvedBy: { id: string; code: string; name: string } | null;
      }[];
    }>(`/quality/flags?${new URLSearchParams(params)}`),

  resolveFlag: (flagId: string, resolution: string, note?: string) =>
    request<unknown>(`/quality/flags/${flagId}/resolve`, {
      method: 'POST',
      body: { resolution, ...(note ? { note } : {}) },
    }),

  verificationProgress: () =>
    request<{
      eligibleInterviews: number;
      byMode: {
        mode: string;
        targetShare: number;
        targetCount: number;
        assigned: number;
        completed: number;
        pending: number;
        achievedShare: number;
        outcomes: { outcome: string | null; count: number }[];
      }[];
    }>('/quality/verification/progress'),

  drawVerification: (mode: 'telefonica' | 'presencial', assignToId?: string) =>
    request<{ selected: number; eligible: number }>('/quality/verification/draw', {
      method: 'POST',
      body: { mode, ...(assignToId ? { assignToId } : {}) },
    }),

  verificationQueue: (params: Record<string, string> = {}) =>
    request<
      {
        id: string;
        mode: string;
        assignedTo: { id: string; code: string; name: string } | null;
        dueFrom: string | null;
        dueTo: string | null;
        overdue: boolean;
        attempts: number;
        outcome: string | null;
        completedAt: string | null;
        note: string | null;
        interview: {
          id: string;
          clientUuid: string;
          startedAt: string;
          durationMinutes: number | null;
          qualityFlags: string[];
          encuestador: { id: string; code: string; name: string };
          cluster: { id: string; seccion: number; reference: string };
          phoneMasked: string;
          contactPurged: boolean;
        };
      }[]
    >(`/quality/verification/queue?${new URLSearchParams(params)}`),

  verificationScript: (verificationId: string) =>
    request<{
      verificationId: string;
      interview: {
        id: string;
        startedAt: string;
        finishedAt: string | null;
        encuestador: { code: string; name: string };
        cluster: { seccion: number; reference: string };
      };
      keyAnswers: {
        questionCode: string;
        questionText: string;
        capturedAnswers: {
          optionCode: string | null;
          optionLabel: string | null;
          numericValue: number | null;
          textValue: string | null;
        }[];
      }[];
    }>(`/quality/verification/${verificationId}/script`),

  completeVerification: (verificationId: string, body: Record<string, unknown>) =>
    request<unknown>(`/quality/verification/${verificationId}/complete`, {
      method: 'POST',
      body,
    }),

  contact: (interviewId: string) =>
    request<{ hasContact: boolean; name: string | null; phone: string | null; purgedAt: string | null }>(
      `/quality/interviews/${interviewId}/contact`,
    ),

  annulments: (params: Record<string, string> = {}) =>
    request<
      {
        id: string;
        reason: string;
        note: string | null;
        status: string;
        requestedAt: string;
        approvedAt: string | null;
        requestedBy: { id: string; code: string; name: string } | null;
        approvedBy: { id: string; code: string; name: string } | null;
        interview: {
          id: string;
          clientUuid: string;
          status: string;
          durationMinutes: number | null;
          qualityFlags: string[];
          encuestador: { id: string; code: string; name: string };
          cluster: { id: string; seccion: number };
        };
      }[]
    >(`/quality/annulments?${new URLSearchParams(params)}`),

  requestAnnulment: (interviewId: string, reason: string, note?: string) =>
    request<unknown>(`/quality/interviews/${interviewId}/annul`, {
      method: 'POST',
      body: { reason, ...(note ? { note } : {}) },
    }),

  decideAnnulment: (annulmentId: string, approve: boolean, note?: string) =>
    request<unknown>(`/quality/annulments/${annulmentId}/decide`, {
      method: 'POST',
      body: { approve, ...(note ? { note } : {}) },
    }),

  piiStatus: () =>
    request<{
      withContact: number;
      purged: number;
      pendingPhoneVerifications: number;
      safeToPurge: boolean;
      blockingReason: string | null;
    }>('/quality/pii/status'),

  purgePii: (reason: string, force: boolean) =>
    request<{ purged: number }>('/quality/pii/purge', { method: 'POST', body: { reason, force } }),

  /* ------------------------- Consumo de datos --------------------------- */

  dataUsage: () =>
    request<{
      totals: { movil: number; wifi: number; desconocida: number };
      porEncuestador: {
        userId: string;
        code: string;
        name: string;
        role: string | null;
        movil: number;
        wifi: number;
        desconocida: number;
        lastSeen: string | null;
      }[];
      isps: { asn: number; name: string; registeredAs: string; kind: string; notes?: string }[];
      /** ASN vistos que no están en la tabla: su consumo NO se está contando. */
      sinClasificar: { asn: number | null; bytes: number; muestras: number }[];
    }>('/telemetry/data-usage'),

  /* ---------------------------- Instrumento ----------------------------- */
  setWaveStatus: (code: string, status: string, reason: string) =>
    request<{ code: string; status: string; changed: boolean; from?: string }>(
      `/instrument/waves/${code}/status`,
      { method: 'PATCH', body: { status, reason } },
    ),

  instrumentPanel: () =>
    request<{
      wave: { id: string; code: string; label: string; status: string; targetN: number };
      instrumentId: string;
      version: string;
      methodologyVersion: string;
      estimatedMinutes: { min: number; max: number };
      /** Expediente de §9.6: qué cambió respecto a la metodología publicada. */
      changeLog: { version: string; date: string; note: string }[];
      blocks: {
        id: number;
        title: string;
        estimatedMinutes: number;
        estimatedMinutesMax: number | null;
        readAloudIntro: string | null;
        orderingRationale: string | null;
        isCore: boolean;
        questionCount: number;
        questions: {
          code: string;
          kind: string;
          text: string;
          interviewerNote: string | null;
          methodologyNote: string | null;
          objectives: number[];
          optionCount: number;
          rowSource: string | null;
          rotateRows: boolean;
          rotateOptions: boolean;
          required: boolean;
          hasSkipLogic: boolean;
          terminates: boolean;
          trimKey: string | null;
          codebookId: string | null;
          codeInOffice: boolean;
          isPii: boolean;
          isVerificationKey: boolean;
          frozen: boolean;
        }[];
      }[];
      trims: {
        key: string;
        order: number;
        description: string;
        mode: string;
        enabled: boolean;
        rowsKept?: number;
        respondentShare?: number;
        assignmentSize?: number;
      }[];
      experiments: {
        key: string;
        title: string;
        questionCode: string;
        cells: { id: string; personajeId: string; labelText: string; weight: number }[];
        methodologyNote: string;
      }[];
      frozenReactives: string[];
      verificationKeyQuestions: string[];
      officeCodedQuestions: string[];
      piiQuestions: string[];
      personajes: { id: string; cardName: string; referenceParty: string; notes?: string }[];
      totals: {
        blocks: number;
        questions: number;
        estimatedMinutesLow: number;
        estimatedMinutesHigh: number;
      };
    }>('/instrument/panel'),

  toggleTrim: (trimKey: string, enabled: boolean) =>
    request<{ trims: unknown[]; warnings: string[] }>(`/instrument/trims/${trimKey}`, {
      method: 'PATCH',
      body: { enabled },
    }),

  updateExperiment: (cells: unknown[]) =>
    request<{
      experiments: unknown[];
      projection: {
        cellId: string;
        personajeId: string;
        expectedN: number;
        marginOfError: number;
        note: string | null;
      }[];
      warnings: string[];
    }>('/instrument/experiment', { method: 'POST', body: { cells } }),

  /* ---------------------------- Asignaciones ---------------------------- */
  assignments: (params: Record<string, string> = {}) =>
    request<unknown[]>(`/assignments?${new URLSearchParams(params)}`),

  createAssignment: (body: Record<string, unknown>) =>
    request<unknown>('/assignments', { method: 'POST', body }),

  reassign: (id: string, encuestadorId: string, reason: string) =>
    request<unknown>(`/assignments/${id}/reassign`, {
      method: 'POST',
      body: { encuestadorId, reason },
    }),

  cancelAssignment: (id: string, reason: string) =>
    request<unknown>(`/assignments/${id}/cancel`, { method: 'POST', body: { reason } }),

  drawManzanas: (clusterId: string, force = false) =>
    request<unknown[]>(`/assignments/clusters/${clusterId}/draw-manzanas`, {
      method: 'POST',
      body: { force },
    }),

  recalculateDwellings: () =>
    request<{ clusters: number; withSkip: number }>('/assignments/recalculate-dwellings', {
      method: 'POST',
      body: {},
    }),

  drawAllManzanas: () =>
    request<{
      total: number;
      drawn: number;
      skipped: number;
      failed: number;
      results: { clusterId: string; status: string; detail: string }[];
    }>('/assignments/draw-all-manzanas', { method: 'POST', body: {} }),

  /* ------------------------------ Entrevistas --------------------------- */
  interviews: (params: Record<string, string> = {}) =>
    request<{
      total: number;
      items: {
        id: string;
        clientUuid: string;
        status: string;
        isPilot: boolean;
        startedAt: string;
        finishedAt: string | null;
        durationMinutes: number | null;
        detectedFrameSeccion: number | null;
        expectedFrameSeccion: number | null;
        gpsStatus: string | null;
        distanceToExpectedMeters: number | null;
        outOfPolygonReason: string | null;
        qualityFlags: string[];
        nonSubstantiveRatio: number | null;
        thirdPartyPresent: boolean | null;
        experimentCellId: string | null;
        syncedAt: string | null;
        encuestador: { id: string; code: string; name: string };
        cluster: { id: string; seccion: number; reference: string; stratumId: string };
        annulment: { status: string; reason: string } | null;
        _count: { answers: number; verifications: number };
      }[];
    }>(`/interviews?${new URLSearchParams(params)}`),

  interview: (id: string) => request<InterviewDetail>(`/interviews/${id}`),

  relocateInterview: (id: string, clusterId: string, reason: string) =>
    request<{
      id: string;
      clusterId: string;
      expectedFrameSeccion: number;
      gpsStatus: string | null;
      reassignedToAssignment: boolean;
    }>(`/interviews/${id}/cluster`, { method: 'PATCH', body: { clusterId, reason } }),

  setPilot: (id: string, isPilot: boolean, reason: string) =>
    request<{ id: string; isPilot: boolean; changed: boolean }>(`/interviews/${id}/pilot`, {
      method: 'PATCH',
      body: { isPilot, reason },
    }),

  /* ------------------------------ GPS ----------------------------------- */
  detectGps: (body: Record<string, unknown>) =>
    request<GpsDetectionResult>('/geo/detect', { method: 'POST', body }),

  /* ---------------------------- Codificación ---------------------------- */
  codingProgress: () =>
    request<
      {
        questionCode: string;
        questionText: string;
        codebookId: string | null;
        totalResponses: number;
        coded: number;
        pending: number;
        share: number;
      }[]
    >('/coding/progress'),

  codingPending: (questionCode: string) =>
    request<
      { normalized: string; samples: string[]; occurrences: number; answerIds: string[] }[]
    >(`/coding/pending/${questionCode}`),

  codeGroup: (answerIds: string[], assignedCode: string, codebookId: string | null) =>
    request<{ coded: number }>('/coding/code-group', {
      method: 'POST',
      body: { answerIds, assignedCode, codebookId },
    }),

  autoCode: (questionCode: string) =>
    request<{ pending: number; autoCoded: number; remaining: number; note: string }>(
      `/coding/auto-code/${questionCode}`,
      { method: 'POST', body: {} },
    ),

  codebooks: () =>
    request<
      {
        id: string;
        label: string;
        questions: string[];
        allowsOther: boolean;
        entries: { id: string; code: string; label: string; synonyms: string[]; addedAfterPilot: boolean }[];
      }[]
    >('/instrument/codebooks'),

  /* ----------------------------- Exportación ---------------------------- */
  codebookExport: () => request<Record<string, unknown>>('/export/codebook'),

  /* --------------------------- Configuración ---------------------------- */
  settings: () =>
    request<
      {
        key: string;
        value: unknown;
        label: string;
        description: string | null;
        methodologyReference: string | null;
        updatedAt: string;
      }[]
    >('/settings'),

  auditLog: (params: Record<string, string> = {}) =>
    request<{
      total: number;
      items: {
        id: string;
        action: string;
        entityType: string;
        entityId: string | null;
        metadata: unknown;
        ipAddress: string | null;
        createdAt: string;
        user: { id: string; code: string; name: string; role: string } | null;
      }[];
    }>(`/settings/audit-log?${new URLSearchParams(params)}`),

  health: () =>
    request<{
      status: string;
      database: boolean;
      postgis: string;
      gpsDetection: string;
    }>('/health', { anonymous: true }),
};

export interface TargetRow {
  code: string;
  label: string;
  padronShare: number;
  target: number;
  padronCount?: number;
}

export interface DeviationRow {
  code: string;
  label: string;
  target: number;
  achieved: number;
  achievedShare: number;
  padronShare: number;
  deviationPp: number;
  expectedAtCurrentPace: number;
  gapAtCurrentPace: number;
  shareOfTarget: number;
}

/** Descarga un archivo del API conservando el nombre que envía el servidor. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`/api${path}`, {
    headers: session.accessToken ? { Authorization: `Bearer ${session.accessToken}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status, `No se pudo descargar el archivo (${response.status}).`);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/* ------------------------- Detalle de entrevista ----------------------- */

export interface TranscriptAnswer {
  questionCode: string;
  questionText: string | null;
  kind: string | null;
  rowCode: string | null;
  /** Nombre del personaje cuando la pregunta es una batería. */
  rowLabel: string | null;
  rankOrder: number | null;
  optionCode: string | null;
  optionLabel: string | null;
  numericValue: number | null;
  textValue: string | null;
  specifyText: string | null;
  isNonSubstantive: boolean;
}

export interface InterviewDetail {
  id: string;
  clientUuid: string;
  status: string;
  isPilot: boolean;
  startedAt: string;
  finishedAt: string | null;
  durationMinutes: number | null;
  latitude: number | null;
  longitude: number | null;
  accuracyMeters: number | null;
  gpsStatus: string | null;
  detectedSeccion: number | null;
  detectedFrameSeccion: number | null;
  expectedFrameSeccion: number | null;
  distanceToExpectedMeters: number | null;
  outOfPolygonReason: string | null;
  outOfPolygonNote: string | null;
  coloniaDeclared: string | null;
  experimentCellId: string | null;
  observations: string | null;
  qualityFlags: string[];
  nonSubstantiveRatio: number | null;
  encuestador: { id: string; code: string; name: string };
  cluster: { id: string; seccion: number; reference: string | null; stratumId: string };
  wave: { id: string; code: string; label: string };
  instrument: { id: string; version: string };
  household: {
    sequenceNumber: number | null;
    disposition: string | null;
    eligibleAdults: number | null;
    manzanaCvegeo: string | null;
    attempts: number | null;
  } | null;
  contact: { hasContact: boolean; phoneLast4: string | null; purgedAt: string | null };
  flags: { id: string; flag: string; severity: string; resolvedAt: string | null }[];
  verifications: {
    id: string;
    mode: string;
    status: string;
    completedBy: { code: string; name: string } | null;
  }[];
  annulment: {
    status: string;
    reason: string;
    unilateral?: boolean;
    requestedBy: { code: string; name: string } | null;
    approvedBy: { code: string; name: string } | null;
  } | null;
  transcript: {
    blocks: { blockId: number; title: string; answers: TranscriptAnswer[] }[];
    /** Respuestas sin pregunta en la versión con la que se aplicó. */
    unmatched: TranscriptAnswer[];
  };
}

/* ------------------------ Resultados preliminares ---------------------- */

export interface OptionTallyView {
  code: string;
  label: string;
  count: number;
  value?: number;
  declaredIndex?: number;
  nonSubstantive: boolean;
}

export interface QuestionResultView {
  questionCode: string;
  text: string;
  kind: string;
  blockId: number;
  blockTitle: string;
  form: 'categorical' | 'ordinal' | 'battery' | 'battery-wide' | 'multi' | 'numeric' | 'open';
  answered: number;
  substantive: number;
  options: OptionTallyView[];
  rows?: { code: string; label: string; options: OptionTallyView[]; answered: number }[];
  numeric?: { n: number; min: number; max: number; mean: number; median: number };
  mentions?: { label: string; count: number; coded: boolean }[];
}

export interface ResultsPreview {
  instrumentVersion: string;
  filters: {
    applied: { stratum: string | null; gender: string | null; ageGroup: string | null };
    strata: { code: string; label: string }[];
    genders: { code: string; label: string }[];
    ageGroups: { code: string; label: string }[];
  };
  /** Margen efectivo de la base filtrada, en puntos porcentuales. */
  marginOfError: number | null;
  /** Entrevistas completadas que alimentan las frecuencias. SIN PONDERAR. */
  base: number;
  target: number | null;
  questions: QuestionResultView[];
}

/* --------------------------- Jornada de campo -------------------------- */

export interface WorkloadDay {
  day: string;
  completed: number;
  partial: number;
  unsuccessful: number;
  dispositions: Record<string, number>;
  doors: number;
  time: {
    firstAt: string | null;
    lastAt: string | null;
    /** Techo: de la primera marca a la última. */
    spanMinutes: number;
    /** Piso: suma de las duraciones de entrevista. */
    interviewMinutes: number;
    /** Estimación intermedia, con los huecos largos acotados. */
    activeMinutes: number;
    cappedGaps: number;
  };
  interviewsPerHour: number | null;
  doorSuccessRate: number | null;
}

export interface WorkloadInterviewer {
  userId: string;
  code: string;
  name: string;
  isActive: boolean;
  days: WorkloadDay[];
  totals: {
    completed: number;
    partial: number;
    doors: number;
    daysWorked: number;
    spanMinutes: number;
    activeMinutes: number;
    interviewMinutes: number;
  };
}

export interface WorkloadResponse {
  /** Hueco a partir del cual se considera pausa y no trabajo. */
  maxGapMinutes: number;
  interviewers: WorkloadInterviewer[];
}
